(ns hai.notes.sections
  "Shared note-extras UI: the tags/links shown below a note's text, their inline
   \"＋ tag\" / \"＋ link\" add controls, and the editing-mode state machine.

   Used by the web-app's notes-page cards, the dashboard stickies, the quick-note
   modal, and the browser-extension sidebar sticky. The host owns its text field,
   its save/remove handlers, the surface chrome, and — because the tag search
   differs per surface (re-frame + Radix on the web-app, a reactive-query dropdown
   in the sidebar) — supplies it via `render-tag-search`.

   Tags and links both show at rest — each as its own pill up to a small count,
   collapsing to one \"icon +N\" pill past it; editing always shows every pill
   and adds the remove × and the inline \"＋ tag\" / \"＋ link\" controls. There
   are no labeled section dividers — the add controls are dashed ghost chips at
   the end of each row."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as contacts]
   [hai.notes.links :as links]
   [hai.ui.components.chip :as chip-ui]
   [hai.ui.components.tags :as tag-ui]
   [hai.ui.components.tooltip :as tt]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [url.core :as url]))

(def ^:private link-icon (icons/mui-icon :outlined :link))
(def ^:private tag-icon (icons/mui-icon :outlined :label))
(def ^:private note-icon icons/note)
(def ^:private task-icon icons/outlined-task_alt)

(def ^:private collapse-threshold
  "At rest, more than this many links or tags render as one \"icon +N\" pill.
   Editing always shows every pill — each carries its remove control."
  3)

(defn- collapsed?
  [editing? items]
  (and (not editing?) (> (count items) collapse-threshold)))

(o/defstyled count-pill :span
  {:display "inline-flex" :align-items "center" :gap "5px"
   :padding "4px 10px"
   :border-width "1px" :border-style "solid" :border-color t/--note-sage-55
   :border-radius "999px"
   :font-size "12px" :font-weight "600" :line-height "1"
   :color t/--note-sage}
  [:svg {:width "13px" :height "13px" :fill "currentColor"}]
  ([{:keys [icon amount]}]
   [:<> [icon] (str "+" amount)]))

;; Dashed "ghost" chip — the inline "＋ tag" / "＋ link" add control. Sits at the
;; end of its row; clicking it reveals that section's adder in its place.
(o/defstyled add-section-pill :button
  ;; Padding trimmed (vs the chips' 4px) so the 1px border doesn't make this
  ;; taller than the tag chips and nudge the row when it appears in edit.
  {:display "inline-flex" :align-items "center" :gap "4px"
   :align-self "center"
   :padding "3px 10px"
   :border-width "1px" :border-style "dashed" :border-color "rgba(50,64,37,0.3)"
   :border-radius "7px"
   :background "transparent"
   :font-family "inherit"
   :font-size "12px" :font-weight "600" :line-height "1"
   :color "rgba(74,93,63,0.82)"
   :cursor "pointer"
   :-webkit-tap-highlight-color "transparent"
   :transition "background 0.15s, border-color 0.15s"}
  [:&:hover {:background "rgba(50,64,37,0.06)" :border-color "rgba(50,64,37,0.5)"}]
  [:.plus {:font-size "13px" :line-height "1"}])

;; Kept for hosts that bleed a divider to their paper edges; no longer rendered
;; by note-extras (the handoff drops the labeled dividers).
(o/defstyled section-divider :div
  {:display "flex" :align-items "center" :gap "5px"}
  [#{:.divider-lead :.divider-fill}
   {:border-top-width "1px"
    :border-top-style "dashed"
    :border-top-color t/--note-sage-35}]
  [:.divider-lead {:width "12px"}]
  [:.divider-fill {:flex "1"}]
  [:.divider-icon
   {:display "inline-flex" :align-items "center"
    :color t/--note-sage-80}
   [:svg {:width "12px" :height "12px" :fill "currentColor"}]]
  [:.divider-label
   {:font-family "ui-monospace, SFMono-Regular, Menlo, monospace"
    :font-size "10px"
    :letter-spacing "0.08em"
    :text-transform "uppercase"
    :color t/--note-sage-80
    :white-space "nowrap"}]
  ([{:keys [icon label]}]
   [:<>
    [:span.divider-lead]
    [:span.divider-icon [icon]]
    [:span.divider-label label]
    [:span.divider-fill]]))

;; Holds the section blocks, set further apart than items within a block.
(o/defstyled extras-wrap :div
  {:display "flex" :flex-direction "column" :gap "12px"})

;; Blank-note state: the two "＋" triggers share a row instead of stacking.
(o/defstyled add-section-row :div
  {:display "flex" :flex-wrap "wrap" :gap "8px"})

;; A wrapping row of chips with the inline add control at its end. Top-aligned so
;; a slightly taller "＋" ghost can't re-center the chips when edit mode adds it.
;; `.centered` opts into vertical centering — for the links/contacts row, whose
;; open adder is a full input field much taller than the chips beside it.
;; `.adder-space` reserves that open-adder height so the row is the same height
;; whether the dashed ＋ pill or the taller input is showing — opening it then
;; adds no height, so nothing above it in the note reflows.
(o/defstyled chip-row :div
  {:display "flex" :flex-wrap "wrap" :align-items "flex-start" :gap "6px"}
  [:&.centered {:align-items "center"}]
  [:&.adder-space {:min-height "28px"}])

;; A simple wrapping chip row.
(o/defstyled tags-row :div
  :flex :flex-wrap {:gap "4px"})

;; The ⋮ actions trigger on a note surface: sage at rest, kraft on hover. Wraps a
;; raw <button> and styles it as a descendant — a styled Ornament :button doesn't
;; forward refs, so a Radix DropdownMenu trigger built on one never opens.
(o/defstyled kebab-wrap :span
  {:display "inline-flex"}
  [:button
   {:width "24px" :height "24px" :border-radius "6px"
    :border "none" :background "transparent" :padding "0"
    :color "rgba(74,93,63,0.8)" :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :transition "background 0.15s"}
   [:&:hover {:background "rgba(50,64,37,0.1)" :color t/--note-kraft}]
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]])

;; The link chip's label: as much of the URL as fits the row (schemeless
;; host/path, truncated at the end), opening in a new tab.
(o/defstyled link-label :a
  {:color "inherit"
   :text-decoration "underline"
   :text-underline-offset "2px"})

(defn- link-chip
  [{:keys [url on-remove]}]
  [chip-ui/entity-chip
   {:glyph        [link-icon]
    :label        [link-label
                   {:href url :target "_blank" :rel "noopener noreferrer" :title url
                    ;; Don't take focus on click — inside a sticky it would shift
                    ;; the target.
                    :on-mouse-down #(.preventDefault %)
                    :on-click #(.stopPropagation %)}
                   (url/url->display-label url)]
    :remove-label "Remove link"
    :on-remove    (when on-remove #(on-remove url))}])

;; A linked contact/note/task chip's label when the host can route to it: a
;; button that looks exactly like the plain label, underlined on hover like the
;; URL chip.
(o/defstyled entity-open-label :button
  {:padding "0"
   :border "none"
   :background "transparent"
   :font-family "inherit" :font-size "inherit" :font-weight "inherit"
   :color "inherit"
   :text-align "left"
   :cursor "pointer"}
  [:&:hover {:text-decoration "underline" :text-underline-offset "2px"}])

(def ^:private chip-label-max-chars
  "Note bodies are free text; a chip shows the opening of one, not all of it."
  40)

(defn- ->chip-label
  [text fallback]
  (let [flat (-> (or text "") (str/replace #"\s+" " ") str/trim)]
    (cond
      (str/blank? flat)                  fallback
      (> (count flat) chip-label-max-chars) (str (subs flat 0 chip-label-max-chars) "…")
      :else                              flat)))

;; A linked contact, note or task. `on-open` (host-supplied — the surface owns
;; its routing) makes the label clickable; without it the chip is display-only.
(defn- entity-chip
  [{:keys [glyph text fallback tooltip remove-label on-remove on-open]}]
  (let [label (->chip-label text fallback)]
    [chip-ui/entity-chip
     {:glyph        [glyph]
      :label        [tt/tooltip {:content (str tooltip label)}
                     (if on-open
                       ;; A plain :button carrying the styled class by name, not
                       ;; `entity-open-label` itself — an Ornament component
                       ;; doesn't forward refs, which Radix's trigger
                       ;; composition needs, and the tooltip silently never
                       ;; opens (same workaround as `add-trigger` below).
                       [:button
                        {:type "button"
                         :class (str entity-open-label)
                         ;; Don't take focus — inside a sticky, focus-within
                         ;; expands the note and moves the target mid-click.
                         :on-mouse-down #(.preventDefault %)
                         :on-click (fn [e] (.stopPropagation e) (on-open))}
                        label]
                       [:span label])]
      :remove-label remove-label
      :on-remove    on-remove}]))

;; Organisations get the group glyph, individuals the person one — same split
;; the contacts card avatar makes.
(defn- contact-chip
  [{:keys [contact on-remove on-open]}]
  [entity-chip
   {:glyph        (contacts/type-icon contact)
    :text         (:contact/full-name contact)
    :fallback     "Unnamed contact"
    :tooltip      (if (contacts/org? contact)
                    "Linked organisation: "
                    "Linked contact: ")
    :remove-label "Remove contact"
    :on-remove    (when on-remove #(on-remove contact))
    :on-open      (when on-open #(on-open contact))}])

(defn- note-chip
  [{:keys [note on-remove on-open]}]
  [entity-chip
   {:glyph        note-icon
    :text         (:note/text note)
    :fallback     "Untitled note"
    :tooltip      "Linked note: "
    :remove-label "Remove linked note"
    :on-remove    (when on-remove #(on-remove note))
    :on-open      (when on-open #(on-open note))}])

(defn- task-chip
  [{:keys [task on-remove on-open]}]
  [entity-chip
   {:glyph        task-icon
    :text         (:task/title task)
    :fallback     "Untitled task"
    :tooltip      "Linked task: "
    :remove-label "Remove linked task"
    :on-remove    (when on-remove #(on-remove task))
    :on-open      (when on-open #(on-open task))}])

;; The tooltip wraps a plain `:button` (not `add-section-pill` itself, applying
;; its class by name) — a styled Ornament :button doesn't forward refs, which
;; Radix's trigger composition needs (see `kebab-wrap` above).
(defn- add-trigger [{:keys [label on-click tooltip]}]
  [tt/tooltip {:content tooltip}
   [:button
    {:type "button"
     :class (str add-section-pill)
     :on-mouse-down #(.preventDefault %)
     :on-click (fn [_] (on-click))}
    [:span.plus "＋"] label]])

;; A tag is either committed (`:group/uuid` + `:group/display-name`) or a not-yet-
;; saved pick (`:tag/name` only, from an uncommitted draft). Render and key both.
(defn- tag-display-name [tag]
  (or (:group/display-name tag) (:tag/name tag)))

;; `render-tag-search` is a host-supplied fn: given
;; `{:placeholder :auto-focus? :exclude-uuids :exclude-names :on-pick :on-cancel}`
;; it returns the surface's own tag-search element (so its data layer stays local).
(defn- tags-section [{:keys [tags editing? open? on-open on-cancel on-pick on-remove
                             render-tag-search]}]
  (if (collapsed? editing? tags)
    [chip-row [count-pill {:icon tag-icon :amount (count tags)}]]
    ;; Same centered, adder-height row as the links row below, so the two rows
    ;; share one height and line up at rest.
    (into [chip-row {:class (str "centered" (when editing? " adder-space"))}]
          (concat
           (for [tag tags]
             ^{:key (or (:group/uuid tag) (str "new-" (:tag/name tag)))}
             [tag-ui/chip {:label     (tag-ui/with-hash (tag-display-name tag))
                           ;; Pass the whole tag — an uncommitted pick has no uuid.
                           :on-remove (when editing? #(on-remove tag))}])
           (when editing?
             [(if open?
                (render-tag-search
                 {:placeholder   "Search or create a tag…"
                  :auto-focus?   true
                  :exclude-uuids (keep :group/uuid tags)
                  :exclude-names (keep #(some-> (tag-display-name %) str/lower-case) tags)
                  :on-pick       on-pick
                  :on-cancel     on-cancel})
                [add-trigger {:label   "tag"
                              :tooltip "Tag this note to find it with related work"
                              :on-click on-open}])])))))

;; URLs, contacts, notes and tasks share one row/adder: the ＋ link input doubles
;; as an entity search (see `links/link-adder`'s `render-entity-search`), so
;; picking any of the three and pasting a URL all land here as chips.
(defn- links-section [{:keys [link-urls contacts linked-notes linked-tasks note-uuid
                              editing? open? on-open on-cancel
                              on-add on-remove on-add-contact on-remove-contact
                              on-add-note on-remove-note on-add-task on-remove-task
                              on-open-entity render-entity-search]}]
  (let [items (concat link-urls contacts linked-notes linked-tasks)]
    (if (collapsed? editing? items)
      [chip-row [count-pill {:icon link-icon :amount (count items)}]]
      (into [chip-row {:class (str "centered" (when editing? " adder-space"))}]
            (concat
             (for [contact contacts]
               ^{:key (:contact/uuid contact)}
               [contact-chip {:contact   contact
                              :on-remove (when editing? on-remove-contact)
                              :on-open   on-open-entity}])
             (for [note linked-notes]
               ^{:key (:note/uuid note)}
               [note-chip {:note note
                           :on-remove (when editing? on-remove-note)
                           :on-open on-open-entity}])
             (for [task linked-tasks]
               ^{:key (:task/uuid task)}
               [task-chip {:task task
                           :on-remove (when editing? on-remove-task)
                           :on-open on-open-entity}])
             (for [link-url link-urls]
               ^{:key link-url}
               [link-chip {:url link-url :on-remove (when editing? on-remove)}])
             (when editing?
               [(if open?
                  [links/link-adder
                   {:always-open? true
                    :on-add on-add
                    :on-cancel on-cancel
                    :render-entity-search render-entity-search
                    ;; One picker, three destinations — routed by the id attr the
                    ;; picked entity carries.
                    :on-pick-entity (fn [entity]
                                      (cond
                                        (:contact/uuid entity) (on-add-contact entity)
                                        (:note/uuid entity)    (on-add-note entity)
                                        (:task/uuid entity)    (on-add-task entity)))
                    ;; The note itself is excluded too — a self-link is always a
                    ;; mistake, and the backend drops it anyway.
                    :linked-uuids (cond->> (concat (keep :contact/uuid contacts)
                                                   (keep :note/uuid linked-notes)
                                                   (keep :task/uuid linked-tasks))
                                    note-uuid (cons note-uuid))}]
                  [add-trigger {:label   "link"
                                :tooltip "Attach a page, contact, note or task to this note"
                                :on-click on-open}])]))))))

(defn note-extras
  "Tags and links (URLs, contacts, notes, tasks) for a note. All show at rest as
   chips, collapsing to an \"icon +N\" pill past a small count; while editing
   every chip shows and each row gains a dashed \"＋\" control (and a section's
   adder replaces it once opened). On a blank note being edited the two \"＋\"
   triggers share a row. Renders nothing when there's nothing to show.

   `render-tag-search` is required whenever tags can show (see `tags-section`).
   `render-entity-search`, if supplied, turns the ＋ link input into a
   searchable contact/note/task dropdown (see `links-section` /
   `links/link-adder`); omit it to keep the plain URL-only adder.
   `on-open-entity`, if supplied, is called with the linked note/task map when
   its chip is clicked — the host owns routing."
  [{:keys [editing? tags link-urls contacts linked-notes linked-tasks note-uuid
           open-set open-section! close-section!
           on-add-tag on-remove-tag on-add-link on-remove-link render-tag-search
           on-add-contact on-remove-contact on-add-note on-remove-note
           on-add-task on-remove-task on-open-entity render-entity-search]}]
  (if (and editing? (empty? tags) (empty? link-urls) (empty? contacts)
           (empty? linked-notes) (empty? linked-tasks) (empty? open-set))
    [add-section-row
     [add-trigger {:label    "tag"
                   :tooltip  "Tag this note to find it with related work"
                   :on-click #(open-section! :tags)}]
     [add-trigger {:label    "link"
                   :tooltip  "Attach a page or source to this note"
                   :on-click #(open-section! :links)}]]
    (let [tags-block  (when (or editing? (seq tags))
                        [tags-section {:tags              tags
                                       :editing?          editing?
                                       :open?             (contains? open-set :tags)
                                       :on-open           #(open-section! :tags)
                                       :on-cancel         #(close-section! :tags)
                                       :on-pick           on-add-tag
                                       :on-remove         on-remove-tag
                                       :render-tag-search render-tag-search}])
          ;; Links (URLs + linked entities) show at rest as read-only chips;
          ;; editing adds their remove × and the ＋ link control.
          links-block (when (or editing? (seq link-urls) (seq contacts)
                                (seq linked-notes) (seq linked-tasks))
                        [links-section {:link-urls            link-urls
                                        :contacts             contacts
                                        :linked-notes         linked-notes
                                        :linked-tasks         linked-tasks
                                        :note-uuid            note-uuid
                                        :editing?             editing?
                                        :open?                (contains? open-set :links)
                                        :on-open              #(open-section! :links)
                                        :on-cancel            #(close-section! :links)
                                        :on-add               on-add-link
                                        :on-remove            on-remove-link
                                        :on-add-contact       on-add-contact
                                        :on-remove-contact    on-remove-contact
                                        :on-add-note          on-add-note
                                        :on-remove-note       on-remove-note
                                        :on-add-task          on-add-task
                                        :on-remove-task       on-remove-task
                                        :on-open-entity       on-open-entity
                                        :render-entity-search render-entity-search}])
          blocks      (cond-> []
                        tags-block  (conj tags-block)
                        links-block (conj links-block))]
      (when (seq blocks)
        (into [extras-wrap] blocks)))))
